import { computed, inject } from "@angular/core";

import { pipe, switchMap, tap } from "rxjs";

import { LoggerService } from "@core/services/logger.service";
import { AiModelHttpService } from "@features/documentation/services/ai-model-http.service";
import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { AIModel, AIProvider, CreateAIModelDto, UpdateAIModelDto } from "@shared/models/ai-model.model";
import { ToastService } from "@shared/services/toast.service";

interface AiModelsState {
  models: AIModel[];
  providers: AIProvider[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  searchQuery: string;
}

const initialState: AiModelsState = {
  models: [],
  providers: [],
  loading: false,
  loaded: false,
  error: null,
  searchQuery: ""
};

export const AiModelsStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed(({ models, searchQuery }) => ({
    filteredModels: computed(() => {
      const query = searchQuery().toLowerCase().trim();
      if (!query) return models();
      return models().filter(
        (m) =>
          m.label.toLowerCase().includes(query) ||
          m.slug.toLowerCase().includes(query) ||
          m.provider?.label.toLowerCase().includes(query)
      );
    })
  })),
  withMethods(
    (store, aiModelService = inject(AiModelHttpService), toast = inject(ToastService), logger = inject(LoggerService)) => ({
      setSearchQuery(query: string): void {
        patchState(store, { searchQuery: query });
      },

      loadModels: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          switchMap(() =>
            aiModelService.getAll().pipe(
              tap({
                next: (response) => {
                  // Handle response.data from PaginatedResponse
                  let models = Array.isArray(response) ? response : response.data;
                  // Sort models by label
                  models = models.sort((a, b) => a.label.localeCompare(b.label));
                  patchState(store, { models, loading: false, loaded: true });
                },
                error: (err) => {
                  logger.error("Failed to load AI models", err);
                  patchState(store, { error: "Failed to load AI models", loading: false });
                  toast.error("Failed to load AI models");
                }
              })
            )
          )
        )
      ),

      loadProviders: rxMethod<void>(
        pipe(
          switchMap(() =>
            aiModelService.getProviders().pipe(
              tap({
                next: (providers) => patchState(store, { providers }),
                error: (err) => {
                  logger.error("Failed to load AI providers", err);
                  toast.error("Failed to load AI providers");
                }
              })
            )
          )
        )
      ),

      createModel: rxMethod<CreateAIModelDto>(
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap((dto) =>
            aiModelService.create(dto).pipe(
              tap({
                next: (createdModel) => {
                  patchState(store, (state) => {
                    let newModels = [createdModel, ...state.models];
                    // If created model is default, unset others
                    if (createdModel.isDefault) {
                      newModels = newModels.map((m) => (m.id === createdModel.id ? m : { ...m, isDefault: false }));
                    }
                    if (createdModel.isHelpMeCreateDefault) {
                      newModels = newModels.map((m) => (m.id === createdModel.id ? m : { ...m, isHelpMeCreateDefault: false }));
                    }
                    return { models: newModels, loading: false };
                  });
                  toast.success("AI Model created successfully");
                },
                error: (err) => {
                  logger.error("Failed to create AI model", err);
                  patchState(store, { loading: false });
                  toast.error("Failed to create AI model");
                }
              })
            )
          )
        )
      ),

      updateModel: rxMethod<{ id: string; data: UpdateAIModelDto }>(
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap(({ data }) =>
            aiModelService.update(data).pipe(
              tap({
                next: (updatedModel) => {
                  patchState(store, (state) => {
                    let newModels = state.models.map((m) => (m.id === updatedModel.id ? updatedModel : m));
                    // If updated model is default, unset others
                    if (updatedModel.isDefault) {
                      newModels = newModels.map((m) => (m.id === updatedModel.id ? m : { ...m, isDefault: false }));
                    }
                    if (updatedModel.isHelpMeCreateDefault) {
                      newModels = newModels.map((m) => (m.id === updatedModel.id ? m : { ...m, isHelpMeCreateDefault: false }));
                    }
                    return { models: newModels, loading: false };
                  });
                  toast.success("AI Model updated successfully");
                },
                error: (err) => {
                  logger.error("Failed to update AI model", err);
                  patchState(store, { loading: false });
                  toast.error("Failed to update AI model");
                }
              })
            )
          )
        )
      ),

      deleteModel: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap((id) =>
            aiModelService.deleteModel(id).pipe(
              tap({
                next: () => {
                  patchState(store, (state) => ({
                    models: state.models.filter((m) => m.id !== id),
                    loading: false
                  }));
                  toast.success("AI Model deleted successfully");
                },
                error: (err) => {
                  logger.error("Failed to delete AI model", err);
                  patchState(store, { loading: false });
                  toast.error("Failed to delete AI model");
                }
              })
            )
          )
        )
      )
    })
  )
);
